import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import { ImportedMeme, CustomMemeTemplate } from '../types';

interface CombinedMeme {
    id: string;
    description: string;
    name: string;
    tags: string;
    url_path: string;
    thumb_path: string;
    rating: number;
    category: string;
    date: string;
    locale: string;
    example: string;
    mongo_id: string;
    version: number;
    local_image_path: string;
    local_thumb_path: string;
    created_at: string;
    updated_at: string;
    isCustom?: boolean;
}

interface UseCombinedMemesReturn {
    allMemes: CombinedMeme[];
    shuffledMemes: CombinedMeme[];
    loading: boolean;
    error: string | null;
    totalCount: number;
    loadedCount: number;
    reshuffle: () => void;
}

// Fisher-Yates shuffle algorithm
const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

export const useCombinedMemes = (): UseCombinedMemesReturn => {
    const [allMemes, setAllMemes] = useState<CombinedMeme[]>([]);
    const [shuffledMemes, setShuffledMemes] = useState<CombinedMeme[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [loadedCount, setLoadedCount] = useState(0);

    useEffect(() => {
        loadAllMemes();
    }, []);

    const reshuffle = () => {
        if (allMemes.length > 0) {
            const shuffled = shuffleArray(allMemes);
            setShuffledMemes(shuffled);
        }
    };

    const loadAllMemes = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!supabase) {
                throw new Error('Supabase client not initialized');
            }

            // First, get the total count for imported memes
            const { count: importedCount, error: importedCountError } = await supabase
                .from('imported_memes')
                .select('*', { count: 'exact', head: true });

            if (importedCountError) throw importedCountError;

            // Get total count for custom templates
            const { count: templatesCount, error: templatesCountError } = await supabase
                .from('meme_templates')
                .select('*', { count: 'exact', head: true })
                .eq('category', 'oz_and_tali');

            if (templatesCountError) throw templatesCountError;

            const totalImported = importedCount || 0;
            const totalTemplates = templatesCount || 0;
            const total = totalImported + totalTemplates;

            setTotalCount(total);

            if (total === 0) {
                setAllMemes([]);
                setShuffledMemes([]);
                return;
            }

            // Load imported memes in batches
            const batchSize = 1000; // Supabase's default limit
            const importedBatches = Math.ceil(totalImported / batchSize);
            const allImportedMemes: ImportedMeme[] = [];

            for (let i = 0; i < importedBatches; i++) {
                const from = i * batchSize;
                const to = Math.min(from + batchSize - 1, totalImported - 1);

                const { data, error: batchError } = await supabase
                    .from('imported_memes')
                    .select('*')
                    .range(from, to);

                if (batchError) throw batchError;

                if (data) {
                    allImportedMemes.push(...data);
                }
            }

            // Load custom templates in batches
            const templateBatches = Math.ceil(totalTemplates / batchSize);
            const allCustomTemplates: CustomMemeTemplate[] = [];

            for (let i = 0; i < templateBatches; i++) {
                const from = i * batchSize;
                const to = Math.min(from + batchSize - 1, totalTemplates - 1);

                const { data, error: batchError } = await supabase
                    .from('meme_templates')
                    .select('*')
                    .eq('category', 'oz_and_tali')
                    .range(from, to);

                if (batchError) throw batchError;

                if (data) {
                    allCustomTemplates.push(...data);
                }
            }

            // Convert custom templates to CombinedMeme format
            const convertedTemplates: CombinedMeme[] = allCustomTemplates.map(template => ({
                id: template.id,
                description: template.name,
                name: template.name,
                tags: '',
                url_path: template.image_url,
                thumb_path: template.image_url,
                rating: 0,
                category: 'oz_and_tali',
                date: template.created_at,
                locale: 'he',
                example: '',
                mongo_id: template.id,
                version: 1,
                local_image_path: template.image_url,
                local_thumb_path: template.image_url,
                created_at: template.created_at,
                updated_at: template.created_at,
                isCustom: true
            }));

            // Combine imported memes and custom templates
            const combinedMemes = [
                ...allImportedMemes,
                ...convertedTemplates
            ];

            setLoadedCount(combinedMemes.length);
            setAllMemes(combinedMemes);

            // Shuffle all memes to ensure different offerings on each visit
            const shuffled = shuffleArray(combinedMemes);
            setShuffledMemes(shuffled);

        } catch (err) {
            console.error('Error loading memes:', err);
            setError('Failed to load memes');
        } finally {
            setLoading(false);
        }
    };

    return {
        allMemes,
        shuffledMemes,
        loading,
        error,
        totalCount,
        loadedCount,
        reshuffle,
    };
};
